import { ArrowRightIcon, Checkbox } from '@postidigital/posti-components'
import { observer } from 'mobx-react-lite'
import React, { ChangeEventHandler, MouseEventHandler, useCallback, useContext } from 'react'
import { useTranslation } from 'react-i18next'
import { useHistory } from 'react-router'
import styled, { css } from 'styled-components'

import { FeatureFlags } from '../../featureFlags'
import Localization from '../../locales'
import { StoreContext } from '../../store'
import { ButtonContainer, CancelButton, ContentSection, SaveButton, ServiceContractList } from '../'
import { ResponsiveBody } from '../typography/ResponsiveBody'
import { ResponsiveHeadline } from '../typography/ResponsiveHeadline'
import { ResponsiveLink } from '../typography/ResponsiveLink'
import { checkboxLabel } from './checkboxLabel'

const MarketingDetailsExpanderContentComponent: React.FC = () => {
  const history = useHistory()
  const { userStore, terminateAccountViewStore, infoStore } = useContext(StoreContext)
  const { t } = useTranslation()
  let feedbackUrl = window['postienv']?.feedbackLinks?.[Localization.CurrentLocale]

  if (!feedbackUrl) {
    feedbackUrl = 'https://yhteystietoni.posti.fi/ythp/datarequest?lang=' + Localization.CurrentLocale
  }

  const handleCancelClick = useCallback(() => {
    userStore.setExpanderOpen(false)
    userStore.restoreOriginalMarketingPermissions()
    window.scrollTo({ top: 0, left: 0, behavior: 'smooth' })
  }, [userStore])

  const handleSave = useCallback(async () => {
    if (userStore.savingChanges) {
      return
    }

    await userStore.saveMarketingChanges()
    userStore.setExpanderOpen(false)
    window.scrollTo({ top: 0, left: 0, behavior: 'smooth' })
  }, [userStore])

  const handleCheckboxChange: ChangeEventHandler<HTMLInputElement> = useCallback(
    (event) => {
      const { name } = event.target
      userStore.toggleMarketingPermission(name)
    },
    [userStore]
  )

  const renderCheckbox = useCallback(
    ({ id, name, labelText, description }: { id: string; name: string; labelText: string; description?: string }) => {
      return (
        <Checkbox
          large
          id={id}
          name={name}
          value={name}
          label={checkboxLabel(labelText, description)}
          labelPosition="right"
          onChange={handleCheckboxChange}
          checked={userStore.marketingPermissions.includes(name)}
          style={{ padding: '4px 0', display: 'block' }}
          disabled={infoStore.isPartialConsumer}
        />
      )
    },
    [handleCheckboxChange, userStore.marketingPermissions, infoStore.isPartialConsumer]
  )

  const handleDeleteAccountClick: MouseEventHandler<HTMLAnchorElement> = useCallback(
    (event) => {
      const to = event.currentTarget.getAttribute('href')

      event.preventDefault()
      event.nativeEvent.stopImmediatePropagation()

      if (!to) {
        return null
      }

      terminateAccountViewStore.initialize(infoStore, userStore)
      history.push(to)
    },
    [history, infoStore, terminateAccountViewStore, userStore]
  )

  return (
    <ContentSection gap="lg" top={false}>
      <ContentSection gap="md">
        <ResponsiveHeadline as="h1">{t('detailsView.markConsent')}</ResponsiveHeadline>
        {t(`detailsView.markConsentInfo`)}
        <b>{t(`detailsView.byEmailHeader`)}</b>
        {renderCheckbox({
          id: 'email_marketing_box',
          name: 'EMAIL_MARKETING',
          labelText: t(`detailsView.byEmailDescription`),
        })}
        <b>{t(`detailsView.digitalMarketingHeader`)}</b>
        {renderCheckbox({
          id: 'digital_marketing_box',
          name: 'DIGITAL_MARKETING',
          labelText: t(`detailsView.digitalMarketingDescription`),
          description: t(`detailsView.digitalMarketingDescriptionSmall`),
        })}
        <b>{t(`detailsView.partnerHeader`)}</b>
        {renderCheckbox({
          id: 'partner_phone_marketing_box',
          name: 'PARTNER_PHONE',
          labelText: t(`detailsView.byPhoneDescription`),
        })}
        {renderCheckbox({
          id: 'partner_mail_marketing_box',
          name: 'PARTNER_MAIL',
          labelText: t(`detailsView.byMailDescription`),
        })}
      </ContentSection>
      <ContentSection gap="md">
        <ResponsiveHeadline>{t('detailsView.consentCustomerHeader')}</ResponsiveHeadline>
        {t(`detailsView.consentCustomerInfo`)}
        <b>{t(`detailsView.profilingHeader`)}</b>
        {renderCheckbox({
          id: 'consent_profiling',
          name: 'PROFILING',
          labelText: t(`detailsView.profilingDescription`),
          description: t(`detailsView.profilingDescriptionSmall`),
        })}
        <b>{t(`detailsView.emailAnnouncementsHeader`)}</b>
        {renderCheckbox({
          id: 'email_announcement',
          name: 'EMAIL_ANNOUNCEMENTS',
          labelText: t(`detailsView.emailAnnouncementsDescription`),
          description: t(`detailsView.emailAnnouncementsDescriptionSmall`),
        })}
        <b>{t(`detailsView.customerSurveysHeader`)}</b>
        {renderCheckbox({
          id: 'customer_surveys',
          name: 'CUSTOMER_SURVEYS',
          labelText: t(`detailsView.customerSurveysDescription`),
        })}
      </ContentSection>
      <ContentSection gap="md">
        <ResponsiveHeadline>{t('detailsView.dataSharing')}</ResponsiveHeadline>
        {t(`detailsView.updateContactHeader`)}
        {renderCheckbox({
          id: 'partner_updatecontact_box',
          name: 'PARTNER_UPDATECONTACTINFO',
          labelText: t('detailsView.updateContactDescription'),
          description: t('detailsView.updateContactDescriptionSmall'),
        })}
      </ContentSection>
      <Divider />
      <ServiceContractList items={userStore.identities.consumerIdentity.contracts} />
      <div style={{ display: 'flex', flexDirection: 'column' }}>
        <ResponsiveBody>{t(`detailsView.accountTerminationDescription`)}</ResponsiveBody>
        <ResponsiveLink
          href="/terminateAccount"
          onClick={handleDeleteAccountClick}
          icon={ArrowRightIcon}
          iconPosition="right"
        >
          {t(`detailsView.accountTerminationLink`)}
        </ResponsiveLink>

        {FeatureFlags.FeedbackLinksEnabled && infoStore.userInfo.consumerAccountExists && (
          <>
            <ResponsiveLink href={feedbackUrl} icon={ArrowRightIcon} iconPosition="right" target="_blank">
              {t('sideMenu.feedback.link')}
            </ResponsiveLink>
          </>
        )}
      </div>
      <ButtonContainer position="start">
        <SaveButton
          id="save_consumer_details_button"
          disabled={!userStore.changesMade || infoStore.isPartialConsumer}
          onClick={handleSave}
        />
        <CancelButton
          id="cancel_consumer_details_button"
          disabled={!userStore.changesMade || infoStore.isPartialConsumer}
          onClick={handleCancelClick}
        />
      </ButtonContainer>
    </ContentSection>
  )
}

export const MarketingDetailsExpanderContent = observer(MarketingDetailsExpanderContentComponent)

const Divider = styled('div')(
  ({ theme: { spacing } }) => css`
    max-height: 1px;
    height: 1px;
    background: #b5b8ba;
    width: 100%;
    margin: ${spacing.space4}rem 0;
  `
)
